import { StateCreator } from "zustand";
import {
  DeliveryTypeOptionsIdType,
  DELIVERY_TYPE_OPTIONS,
} from "@shared/constants";
import { UserBasedVenueSerializer } from "@app-contexts/dashboard/features/venues/data/types/user-venue";
import { type Store } from "../useStore";

export type PdfTicketLayout = "full" | "thermal";

export const LANGUAGE_MODES = {
  PER_TRANSACTION: "per_transaction",
  ALL_TRANSACTIONS: "all_transactions",
} as const;

export type LanguageMode = (typeof LANGUAGE_MODES)[keyof typeof LANGUAGE_MODES];

export type BoxOfficeConfigType = {
  auto_checkin: boolean;
  ticket_delivery: DeliveryTypeOptionsIdType;
  receipt_delivery: DeliveryTypeOptionsIdType;
  payment_type?: number;
  other_custom_type?: number;
  is_delivery_method_selection_required: boolean;
  thermal_template?: UserBasedVenueSerializer["thermal_templates"][number];
  /** OS system printer name — unique device identifier */
  selected_thermal_printer: string;
  /** OS system printer name — unique device identifier */
  selected_receipt_printer: string;
  pinned_events: { [key: string]: string[] };
  pinned_products: string[];
  pinned_memberships: string[];
  /** Remember user's Square terminal preference for box office checkout */
  prefers_square_terminal?: boolean;
  /** Remember if ticket printer settings accordion is expanded */
  ticket_printer_settings_expanded?: boolean;
  /** Remember if receipt printer settings accordion is expanded */
  receipt_printer_settings_expanded?: boolean;
  /** PDF layout preference when "Save as PDF" is selected for tickets */
  pdf_ticket_layout: PdfTicketLayout;
  /** Per-transaction language override (session-only, not persisted) */
  language?: "en" | "fr";
  /** Whether the operator picks language once in settings or per-transaction at checkout */
  language_mode?: LanguageMode;
  /** Persisted default language when language_mode is "all_transactions" */
  default_language?: "en" | "fr";
};

type State = {
  boxOfficeConfig: BoxOfficeConfigType;
};

type Actions = {
  clearBoxOfficeConfig(): void;
  setBoxOfficeConfig(boxOfficeConfig: State["boxOfficeConfig"]): void;
};

export type BoxOfficeConfigSlice = State & Actions;

const boxOfficeConfigInitialState: State["boxOfficeConfig"] = {
  auto_checkin: false,
  receipt_delivery: DELIVERY_TYPE_OPTIONS.TYPE_PRINT.id,
  ticket_delivery: DELIVERY_TYPE_OPTIONS.TYPE_NONE.id,
  is_delivery_method_selection_required: false,
  thermal_template: undefined,
  selected_thermal_printer: "",
  selected_receipt_printer: "",
  pinned_events: {},
  pinned_products: [],
  pinned_memberships: [],
  prefers_square_terminal: false,
  ticket_printer_settings_expanded: false,
  receipt_printer_settings_expanded: false,
  pdf_ticket_layout: "full",
  language_mode: undefined,
  default_language: undefined,
};

const createBoxOfficeConfigSlice: StateCreator<
  Store,
  [["zustand/persist", unknown]],
  [],
  BoxOfficeConfigSlice
> = (set) => ({
  boxOfficeConfig: boxOfficeConfigInitialState,

  setBoxOfficeConfig(boxOfficeConfig) {
    set({ boxOfficeConfig });
  },

  clearBoxOfficeConfig() {
    set({ boxOfficeConfig: boxOfficeConfigInitialState });
  },
});

export default createBoxOfficeConfigSlice;
